import { ArchiveOutlined, Groups } from "@mui/icons-material";
import { Box, CardActionArea, CardContent, CardHeader, Grid, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import CRMMenu from "../../../App/Components/Menu/CRMMenu";
import { AppFeatureLookup } from "../../../App/Constants/AbacConstants";
import { crmLiteUpgradeMessage } from "../../../App/Constants/CrmLiteConstants";
import { useFeaturePermission } from "../../../App/State/Hooks/abacHooks";
import { useUserProfileStore } from "../../../App/State/Stores/UserProfileStore";
import { IGroup } from "../../../App/Typings/Groups";
import { GroupChip, getRuleFields } from "./DisplayGroupList";

export const DisplayGroupCard = ({
  group,
  handleArchiveGroup,
  containsDynamic,
}: {
  group: IGroup;
  handleArchiveGroup: (groupId: string) => void;
  containsDynamic: boolean;
}) => {
  const archiveGroupTooltip = `Archive ${group.name}`;
  const accessViewGroup = useFeaturePermission(AppFeatureLookup.GroupsViewGroupProfile);
  const viewGroupMessage = accessViewGroup ? "View Group details" : crmLiteUpgradeMessage;
  const { getUserAdjustedDateFromNow, getUserAdjustedDate } = useUserProfileStore();

  return (
    <CardActionArea
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
      disabled={!accessViewGroup}
      aria-label={viewGroupMessage}
      data-test={`candidategroup-viewgroup-${group.id}`}
      component={Link}
      to={`/candidategroups/${group.id}`}
      disableRipple={true}
    >
      <CardHeader
        title={
          <Stack direction="row" justifyContent={"flex-start"}>
            <Typography
              data-test="candidategroup-grouplabel"
              sx={{ fontSize: ".8em", fontWeight: "bold" }}
              noWrap
              title={group.name}
            >
              {group.name}
            </Typography>
            <Box sx={{ marginLeft: "auto", marginTop: "-5px", marginRight: "-5px" }}>
              <CRMMenu
                title="Group Actions"
                id={`group-menu-${group.id}`}
                links={[
                  {
                    dataTestId: `candidategroup-viewgroup-menu-${group.id}`,
                    title: "View Group",
                    tooltip: viewGroupMessage,
                    disabled: !accessViewGroup,
                    href: `/candidategroups/${group.id}`,
                    icon: <Groups />,
                    isLink: true,
                  },
                  {
                    dataTestId: `candidategroup-archivegroup-${group.id}`,
                    title: "Archive",
                    icon: <ArchiveOutlined />,
                    tooltip: archiveGroupTooltip,
                    onClick: (ev) => {
                      ev.stopPropagation();
                      handleArchiveGroup(group.id);
                    },
                  },
                ]}
              />
            </Box>
          </Stack>
        }
        sx={{ width: "100%", "> .MuiCardHeader-content": { maxWidth: "100%" } }}
      />
      <CardContent sx={{ paddingTop: "0 !important", paddingBottom: "0 !important", width: "100%" }}>
        <Grid container rowSpacing={0.25}>
          <Grid item sx={{ mb: 2 }} xs={12} md={4}>
            <GroupChip group={group} />
          </Grid>
          <Grid item sx={{ mb: 2 }} xs={12} md={4}>
            <Typography sx={{ fontSize: "0.75em", fontWeight: "bold" }}>Candidates:</Typography>
            <Typography fontSize={".75em"} data-test="candidategroup-numberofcandidates">
              {group.memberCount}
            </Typography>
          </Grid>
          <Grid item sx={{ mb: 2 }} xs={12} md={4}>
            <Typography sx={{ fontSize: "0.75em", fontWeight: "bold" }}>Last Updated:</Typography>
            <Typography
              fontSize={".75em"}
              data-test="candidategroup-lastupdateddate"
              title={getUserAdjustedDate(group.lastUpdateDate).format("MM/DD/YYYY h:mm A")}
            >
              {getUserAdjustedDateFromNow(group.lastUpdateDate)}
            </Typography>
          </Grid>
          <Grid item sx={{ mb: 2 }} xs={12} md={4}>
            <Typography sx={{ fontSize: "0.75em", fontWeight: "bold" }}>Created:</Typography>
            <Typography
              fontSize={".75em"}
              data-test="candidategroup-createdDate"
              title={getUserAdjustedDate(group.createdDate).format("MM/DD/YYYY h:mm A")}
            >
              {getUserAdjustedDateFromNow(group.createdDate)}
            </Typography>
          </Grid>
          <Grid item sx={{ mb: 2 }} xs={12} md={4}>
            <Typography sx={{ fontSize: "0.75em", fontWeight: "bold" }}>Created By:</Typography>
            <Typography fontSize={".75em"} data-test="candidategroup-createdby">
              {group.creatorNickname}
            </Typography>
          </Grid>
          {containsDynamic && (
            <Grid item sx={{ mb: 2 }} xs={12} md={12}>
              {group.isDynamic ? (
                <>
                  <Typography sx={{ fontSize: "0.75em", fontWeight: "bold" }}>Rules:</Typography>
                  <Typography
                    fontSize={".75em"}
                    data-test="candidategroup-rules"
                    noWrap
                    title={getRuleFields(group.rules).join(", ")}
                  >
                    {getRuleFields(group.rules).join(", ")}
                  </Typography>
                </>
              ) : (
                <Box minHeight={40}></Box>
              )}
            </Grid>
          )}
        </Grid>
      </CardContent>
    </CardActionArea>
  );
};

export default DisplayGroupCard;
